import React, { memo } from 'react'
import type { Props as SelectProps, OptionProps, SingleValueProps } from 'react-select'
import Select, { components } from 'react-select'
import styled from 'styled-components'

import { SelectOptionsWrapper as SelectOptionsWrapperBase } from './style'
import theme from 'vars/theme'
import { compareValue } from 'utils/commonFunctions'

// JS styled component (./style.js) lacks typed custom props; widen with a type-only cast
const SelectOptionsWrapper = SelectOptionsWrapperBase as React.FC<
  React.PropsWithChildren<{
    width?: number | string
    color?: string
    isError?: boolean
    style?: React.CSSProperties
    className?: string
    'data-dg-id'?: string
    'data-csv-upload-id'?: string
  }>
>

const StyledSelect = styled(Select)`
  .mono-select__control {
    background-color: white;
    border: 1px solid #cccccc;
  }

  .mono-select__menu {
    background-color: white;
    color: #505155;
    .mono-select__option {
      color: #505155;
    }

    .mono-select__option--is-focused {
      background-color: rgba(77, 79, 83, 0.2);
    }

    .mono-select__option--is-selected {
      color: white;
      background-color: #505155;
    }
  }

  .mono-select__single-value {
    color: #505155;
  }

  .mono-select__indicator > svg {
    fill: ${theme.blueMedium5};
  }
`

type OptionType = {
  label: string
  value: string
  'data-dg-id'?: string
}

const CustomOption = (props: OptionProps<OptionType, false>) => (
  <components.Option
    {...props}
    innerProps={{
      ...props.innerProps,
      ...(props.data['data-dg-id'] ? { 'data-dg-id': props.data['data-dg-id'] } : {}),
    }}
  />
)

type SelectPropsWithDgId = { singleValueDataDgId?: string }

// WHY: react-select v3 の SingleValue は innerProps を DOM に spread するため、既定実装を包むだけで
//      属性を足せる（getStyles / classNamePrefix のクラスを維持したまま dg-id を通せる）
// NOTE: 値は共通コンポーネント側で決めず、使用側から singleValueDataDgId で渡す（他画面への誤付与防止）
const CustomSingleValue = (props: SingleValueProps<OptionType>) => {
  // NOTE: selectProps はインデックスシグネチャで any になるため、ローカル型を挟んで prop 名の綴りを結び付ける
  const { singleValueDataDgId: dataDgId } = props.selectProps as SelectPropsWithDgId

  return (
    <components.SingleValue
      {...props}
      innerProps={{
        ...props.innerProps,
        ...(dataDgId ? { 'data-dg-id': dataDgId } : {}),
      }}
    />
  )
}

const selectComponents = { Option: CustomOption, SingleValue: CustomSingleValue }

type Props = {
  width?: string
  className?: string
  style?: React.CSSProperties
  hasClass?: boolean
  color?: string
  isError?: boolean
  label?: string
  required?: boolean
  value: string | number
  csvUploadId?: string
  /** 選択中の値（.mono-select__single-value）に付与する data-dg-id */
  singleValueDataDgId?: string
} & Omit<SelectProps<OptionType, false>, 'value'>

const CustomSelect: React.FC<Props> = (props) => {
  const {
    width,
    className,
    style,
    options,
    placeholder,
    hasClass,
    name,
    color,
    isSearchable,
    isError,
    isDisabled,
    onChange,
    value,
    csvUploadId,
    singleValueDataDgId,
  } = props

  const valueSelect = React.useMemo(() => {
    if (!options) return null

    return options.find((option: OptionType) => compareValue(option.value, value))
  }, [options, value])

  return (
    <div className="d-flex flex-shrink-0" style={{ width }} data-csv-upload-id={csvUploadId}>
      <div className="flex-column align-items-start w-100">
        <SelectOptionsWrapper
          width={width}
          className={className}
          style={style}
          color={color}
          isError={isError}
          data-csv-upload-id={csvUploadId ? `${csvUploadId}-options` : undefined}
        >
          <StyledSelect
            className={hasClass ? 'ml-1' : ''}
            classNamePrefix="mono-select"
            isClearable={false}
            name={name}
            options={options}
            value={valueSelect}
            placeholder={placeholder}
            onChange={onChange}
            isSearchable={isSearchable}
            isDisabled={isDisabled}
            menuPlacement="auto"
            noOptionsMessage={() => '選択値なし'}
            data-csv-upload-id={csvUploadId ? `${csvUploadId}-option-list` : undefined}
            components={selectComponents}
            singleValueDataDgId={singleValueDataDgId}
          />
        </SelectOptionsWrapper>
      </div>
    </div>
  )
}

export default memo(CustomSelect)
